import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ConfigProvider, Layout, Button, Avatar, Dropdown, Space, message } from 'antd';
import { UserOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import viVN from 'antd/locale/vi_VN';
import type { MenuProps } from 'antd';

// Import components
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import PermissionRoute from './components/common/PermissionRoute';
import DashboardPage from './components/dashboard/DashboardPage';
import Sidebar from './components/common/Sidebar';
import PermissionManagementPage from './components/permissions/PermissionManagementPage';
import UserManagementPage from './components/users/UserManagementPage';
import RoleManagementPage from './components/roles/RoleManagementPage';
import NganhHocManagementPage from './components/programs/NganhHocManagementPage';
import CourseManagementPage from './components/courses/CourseManagementPage';
import PermissionDebug from './components/debug/PermissionDebug';
import LoginTest from './components/debug/LoginTest';
import AuthTest from './components/debug/AuthTest';
import SubjectManagementPage from './components/subjects/SubjectManagementPage';
import ClassManagementPage from './components/classes/ClassManagementPage';
import LopHocPhanManagementPage from './components/lophocphan/LopHocPhanManagementPage';
import NhomThucHanhPage from './components/nhomthuchanh/NhomThucHanhPage';
import PhieuChamManagementPage from './components/phieucham/PhieuChamManagementPage';
import ExamSessionManagementPage from './components/exams/ExamSessionManagementPage';
import ChamDiemPage from './components/chamdiem/ChamDiemPage';
import SinhVienManagementPage from './components/sinhvien/SinhVienManagementPage';
import ExamScoreReportPage from './components/report/ExamScoreReportPage';
import StudentMakeupManagementPage from './components/makeup/StudentMakeupManagementPage';
import AccountingMakeupManagementPage from './components/makeup/AccountingMakeupManagementPage';
import AttendanceTheoryPage from './components/attendance/AttendanceTheoryPage';
import AttendancePracticePage from './components/attendance/AttendancePracticePage';
import TeachingHoursStatisticPage from './components/thongkegiogiang/TeachingHoursStatisticPage';
import StudentAbsenceStatisticPage from './components/thongkegiogiang/StudentAbsenceStatisticPage';
import TeachingHoursByTeacherPage from './pages/statistics/TeachingHoursByTeacherPage';
import TokenDebugPage from './pages/debug/TokenDebugPage';
import QuestionBankManagementPage from './pages/exam/QuestionBankManagementPage';
import GenerateExamPage from './pages/ExamDepartment/GenerateExamPage';
import ExamHistoryPage from './pages/ExamDepartment/ExamHistoryPage';
import ExamStatisticsPage from './pages/ExamDepartment/ExamStatisticsPage';
import ProfilePage from './pages/ProfilePage';
import MoodleUserManagementPage from './components/moodle/MoodleUserManagementPage';
import MoodleCourseManagementPage from './components/moodle/MoodleCourseManagementPage';
import MoodleCategoryManagementPage from './components/moodle/MoodleCategoryManagementPage';
import GradeInputPage from './pages/GradeInputPage';
import TeacherExtraHoursPage from './pages/TeacherExtraHoursPage';
import TuitionManagementPage from './components/tuition/TuitionManagementPage';
import DailyAttendanceManagementPage from './components/daily-attendance/DailyAttendanceManagementPage';
import DailyAttendanceReportsPage from './components/daily-attendance/DailyAttendanceReportsPage';
import AttendanceStartStatisticPage from './components/statistics/AttendanceStartStatisticPage';
import DailyAttendanceTVDisplay from './components/daily-attendance/DailyAttendanceTVDisplay';
import TeacherWeeklySchedulePage from './pages/TeacherWeeklySchedulePage';
import MeetingAttendancePage from './components/meeting/MeetingAttendancePage';
import MeetingStatisticsPage from './components/meeting/MeetingStatisticsPage';
import VirtualAdvisorManagementPage from './pages/VirtualAdvisorManagementPage';
import QAPairManagement from './pages/Admin/VirtualAdvisor';
import QAPairForm from './pages/Admin/VirtualAdvisor/QAPairForm';
import AIAssistantAdmin from './pages/AIAssistant/AIAssistantAdmin';
import AIChatStatisticsPage from './pages/Admin/AIChatStatistics';
import GradeSyncPage from './pages/Admin/GradeSync';
import FeedbackManagementPage from './pages/Admin/FeedbackManagement';
import NotificationManagementPage from './pages/Admin/NotificationManagement';
import ParkingManagementPage from './components/parking/ParkingManagementPage';
import ParkingLogsPage from './components/parking/ParkingLogsPage';
import ParkingStatsPage from './components/parking/ParkingStatsPage';
import DataSyncManagementPage from './components/sync/DataSyncManagementPage';
import RoomManagementPage from './components/danhmuc/RoomManagementPage';

// Import services
import { AuthService } from './services/authService';
import { AuthProvider } from './contexts/AuthContext';

// Import styles
import './App.css';

const { Header, Content } = Layout;

const SidebarLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState(() => AuthService.getUser());
  const navigate = useNavigate();

  // 🔒 SECURITY: Proactive token expiry check
  useEffect(() => {
    const checkTokenExpiry = () => {
      const expiry = AuthService.getTokenExpiry();
      if (!expiry) return;

      const now = Date.now();
      const timeUntilExpiry = expiry - now;

      // Show warning 5 minutes before expiry
      if (timeUntilExpiry > 0 && timeUntilExpiry <= 5 * 60 * 1000) {
        const minutes = Math.floor(timeUntilExpiry / 60000);
        const exists = document.querySelector('.ant-message-notice');
        if (!exists) {
          message.warning(`Phiên làm việc sẽ hết hạn sau ${minutes} phút. Vui lòng lưu lại công việc.`);
        }
      }

      // Auto-logout 1 minute before expiry
      if (timeUntilExpiry <= 60 * 1000 && timeUntilExpiry > 0) {
        console.error('Token đã hết hạn, đang logout...');
        handleLogout();
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Check every minute
    const interval = setInterval(checkTokenExpiry, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Update user info when authentication state changes
  useEffect(() => {
    const updateUserInfo = () => {
      const user = AuthService.getUser();
      setUserInfo(user);
    };

    // Listen for storage changes (useful for logout in other tabs)
    window.addEventListener('storage', updateUserInfo);

    return () => {
      window.removeEventListener('storage', updateUserInfo);
    };
  }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      await AuthService.logout();
      setUserInfo(null);
      message.success('Đăng xuất thành công');
      // Redirect to login after logout
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      message.error('Có lỗi xảy ra khi đăng xuất');
      // Still redirect even if logout fails
      navigate('/login');
    }
  };

  const handleUserMenuClick = (key: string) => {
    switch (key) {
      case 'profile':
        navigate('/profile');
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Thông tin cá nhân',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      danger: true,
    },
  ];

  // Check if user is authenticated
  const isAuthenticated = AuthService.isAuthenticated();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout style={{
        marginLeft: collapsed ? 80 : 240,
        transition: 'all 0.2s',
        minHeight: '100vh'
      }}>
        <Header style={{
          padding: '0 16px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          height: 64,
          lineHeight: '64px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 40, height: 40 }}
            />
            <span style={{ marginLeft: 16, fontSize: '16px', fontWeight: 500 }}>
              Hệ thống Quản lý Trường học
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Space>
              <span>Xin chào, {userInfo?.FullName || userInfo?.Username || 'Người dùng'}</span>
              <Dropdown menu={{ items: userMenuItems, onClick: ({ key }) => handleUserMenuClick(key) }} trigger={['click']}>
                <Button type="text" style={{ border: 'none' }}>
                  <Avatar size="small" icon={<UserOutlined />} />
                </Button>
              </Dropdown>
            </Space>
          </div>
        </Header>

        <Content style={{
          padding: 0,
          background: '#f0f2f5',
          minHeight: 'calc(100vh - 64px)'
        }}>
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Phân quyền - chỉ admin */}
            <Route path="/permissions/roles" element={
              <PermissionRoute requiredModule="ROLE_PERMISSIONS_MODULE">
                <PermissionManagementPage />
              </PermissionRoute>
            } />
            <Route path="/permissions/users" element={
              <PermissionRoute requiredModule="ROLE_PERMISSIONS_MODULE">
                <PermissionManagementPage />
              </PermissionRoute>
            } />

            {/* Quản lý người dùng */}
            <Route path="/users" element={
              <PermissionRoute requiredModule="USERS_MODULE">
                <UserManagementPage />
              </PermissionRoute>
            } />

            {/* Quản lý vai trò */}
            <Route path="/roles" element={
              <PermissionRoute requiredModule="ROLES_MODULE">
                <RoleManagementPage />
              </PermissionRoute>
            } />

            {/* Danh mục cơ bản */}
            <Route path="/nganhhoc" element={
              <PermissionRoute requiredModule="MAJOR_MODULE">
                <NganhHocManagementPage />
              </PermissionRoute>
            } />
            <Route path="/courses" element={
              <PermissionRoute requiredModule="COURSES_MODULE">
                <CourseManagementPage />
              </PermissionRoute>
            } />
            <Route path="/subjects" element={
              <PermissionRoute requiredModule="SUBJECTS_MODULE">
                <SubjectManagementPage />
              </PermissionRoute>
            } />
            <Route path="/classes" element={
              <PermissionRoute requiredModule="CLASSES_MODULE">
                <ClassManagementPage />
              </PermissionRoute>
            } />
            <Route path="/lop-hoc-phan" element={
              <PermissionRoute requiredModule="CLASS_SECTIONS_MODULE">
                <LopHocPhanManagementPage />
              </PermissionRoute>
            } />

            {/* Đồng bộ dữ liệu */}
            <Route path="/data-sync" element={
              <PermissionRoute requiredModule="COURSES_MODULE">
                <DataSyncManagementPage />
              </PermissionRoute>
            } />

            {/* Đồng bộ điểm */}
            <Route path="/grade-sync" element={
              <PermissionRoute requiredModule="GRADES_MODULE">
                <GradeSyncPage />
              </PermissionRoute>
            } />

            {/* Quản lý phản hồi */}
            <Route path="/admin/feedback" element={
              <PermissionRoute requiredModule="FEEDBACK_MODULE">
                <FeedbackManagementPage />
              </PermissionRoute>
            } />

            {/* Quản lý thông báo */}
            <Route path="/admin/notifications" element={
              <PermissionRoute requiredModule="NOTIFICATION_MODULE">
                <NotificationManagementPage />
              </PermissionRoute>
            } />

            {/* Quản lý học tập */}
            <Route path="/nhom-thuc-hanh" element={
              <PermissionRoute requiredModule="PRACTICE_GROUP_MODULE">
                <NhomThucHanhPage />
              </PermissionRoute>
            } />
            <Route path="/phieu-cham" element={
              <PermissionRoute requiredModule="SCORING_FORM_MODULE">
                <PhieuChamManagementPage />
              </PermissionRoute>
            } />
            <Route path="/exam-sessions" element={
              <PermissionRoute requiredModule="EXAM_SESSION_MODULE">
                <ExamSessionManagementPage />
              </PermissionRoute>
            } />
            <Route path="/cham-diem/:examSessionId" element={
              <PermissionRoute requiredModule="EXAM_SESSION_MODULE">
                <ChamDiemPage />
              </PermissionRoute>
            } />
            <Route path="/sinh-vien" element={
              <PermissionRoute requiredModule="STUDENTS_MODULE">
                <SinhVienManagementPage />
              </PermissionRoute>
            } />

            {/* Quản lý phòng học */}
            <Route path="/rooms" element={
              <PermissionRoute requiredModule="ROOMS_MODULE">
                <RoomManagementPage />
              </PermissionRoute>
            } />

            {/* Điểm danh */}
            <Route path="/attendance/theory" element={
              <PermissionRoute requiredModule="THEORETICAL_ATTENDANCE_MODULE">
                <AttendanceTheoryPage />
              </PermissionRoute>
            } />
            <Route path="/attendance/practice" element={
              <PermissionRoute requiredModule="PRACTICAL_ATTENDANCE_MODULE">
                <AttendancePracticePage />
              </PermissionRoute>
            } />

            {/* Lịch giảng tuần */}
            <Route path="/teacher-weekly-schedule" element={
              <PermissionRoute requiredModule="TEACHER_WEEKLY_SCHEDULE_MODULE">
                <TeacherWeeklySchedulePage />
              </PermissionRoute>
            } />

            {/* Điểm danh cuộc họp */}
            <Route path="/meeting-attendance" element={
              <PermissionRoute requiredModule="MEETING_MANAGEMENT_MODULE">
                <MeetingAttendancePage />
              </PermissionRoute>
            } />
            <Route path="/meeting-statistics" element={
              <PermissionRoute requiredModule="MEETING_STATISTICS_MODULE">
                <MeetingStatisticsPage />
              </PermissionRoute>
            } />

            {/* Điểm danh cán bộ hàng ngày */}
            <Route path="/daily-attendance/management" element={
              <PermissionRoute requiredModule="DAILY_ATTENDANCE_MODULE">
                <DailyAttendanceManagementPage />
              </PermissionRoute>
            } />
            <Route path="/daily-attendance/reports" element={
              <PermissionRoute requiredModule="DAILY_ATTENDANCE_MODULE">
                <DailyAttendanceReportsPage />
              </PermissionRoute>
            } />
            <Route path="/daily-attendance/tv/hospital" element={<DailyAttendanceTVDisplay location="hospital" />} />
            <Route path="/daily-attendance/tv/school" element={<DailyAttendanceTVDisplay location="school" />} />
            <Route path="/daily-attendance/tv" element={<DailyAttendanceTVDisplay location="all" />} />

            {/* Báo cáo */}
            <Route path="/exam-score-report" element={
              <PermissionRoute requiredModule="EXAM_REPORT_MODULE">
                <ExamScoreReportPage />
              </PermissionRoute>
            } />

            {/* Giờ coi/chấm/lâm sàng */}
            <Route path="/teacher/extra-hours" element={
              <PermissionRoute requiredModule="TEACHER_EXTRA_HOURS_MODULE">
                <TeacherExtraHoursPage />
              </PermissionRoute>
            } />

            {/* Học bù */}
            <Route path="/makeup/training" element={
              <PermissionRoute requiredModule="STUDENT_MAKEUP_MODULE">
                <StudentMakeupManagementPage />
              </PermissionRoute>
            } />
            <Route path="/makeup/accounting" element={
              <PermissionRoute requiredModule="ACCOUNTING_MAKEUP_MODULE">
                <AccountingMakeupManagementPage />
              </PermissionRoute>
            } />
            {/* Học phí */}
            <Route path="/tuition" element={
              <PermissionRoute requiredModule="TUITION_MODULE">
                <TuitionManagementPage />
              </PermissionRoute>
            } />

            {/* Quản lý gửi xe */}
            <Route path="/parking/management" element={
              <PermissionRoute requiredModule="VEHICLE_MODULE">
                <ParkingManagementPage />
              </PermissionRoute>
            } />
            <Route path="/parking/logs" element={
              <PermissionRoute requiredModule="VEHICLE_MODULE">
                <ParkingLogsPage />
              </PermissionRoute>
            } />
            <Route path="/parking/stats" element={
              <PermissionRoute requiredModule="VEHICLE_MODULE">
                <ParkingStatsPage />
              </PermissionRoute>
            } />

            {/* Quản lý điểm */}
            <Route path="/grades/input" element={
              <PermissionRoute requiredModule="GRADE_INPUT_MODULE">
                <GradeInputPage />
              </PermissionRoute>
            } />

            {/* Thống kê */}
            <Route path="/statistic/teaching-hours" element={
              <PermissionRoute requiredModule="TEACHING_HOURS_MODULE">
                <TeachingHoursStatisticPage />
              </PermissionRoute>
            } />
            <Route path="/statistic/teaching-hours-by-teacher" element={
              <PermissionRoute requiredModule="TEACHING_HOURS_BY_TEACHER_MODULE">
                <TeachingHoursByTeacherPage />
              </PermissionRoute>
            } />
            <Route path="/teacher/extra-hours" element={
              <PermissionRoute requiredModule="TEACHER_EXTRA_HOURS_MODULE">
                <TeacherExtraHoursPage />
              </PermissionRoute>
            } />
            <Route path="/statistic/student-absence" element={
              <PermissionRoute requiredModule="STUDENT_ABSENCE_MODULE">
                <StudentAbsenceStatisticPage />
              </PermissionRoute>
            } />
            <Route path="/statistic/attendance-start" element={
              <PermissionRoute requiredModule="STATISTICS_MODULE">
                <AttendanceStartStatisticPage />
              </PermissionRoute>
            } />
            <Route path="/statistic/daily-attendance" element={
              <PermissionRoute requiredModule="DAILY_ATTENDANCE_STATISTICS_MODULE">
                <DailyAttendanceReportsPage />
              </PermissionRoute>
            } />

            {/* Khảo thí */}
            <Route path="/exam/question-bank" element={
              <PermissionRoute requiredModule="EXAM_QUESTION_BANK_MODULE">
                <QuestionBankManagementPage />
              </PermissionRoute>
            } />
            <Route path="/exam/generate-exam" element={
              <PermissionRoute requiredModule="EXAM_GENERATE_MODULE">
                <GenerateExamPage />
              </PermissionRoute>
            } />
            <Route path="/exam/history" element={
              <PermissionRoute requiredModule="EXAM_HISTORY_MODULE">
                <ExamHistoryPage />
              </PermissionRoute>
            } />
            <Route path="/exam/statistics" element={
              <PermissionRoute requiredModule="EXAM_STATISTICS_MODULE">
                <ExamStatisticsPage />
              </PermissionRoute>
            } />

            {/* Profile */}
            <Route path="/profile" element={
              <PermissionRoute requiredModule="PROFILE_MODULE">
                <ProfilePage />
              </PermissionRoute>
            } />

            {/* Debug routes - không cần phân quyền */}
            <Route path="/debug/permissions" element={<PermissionDebug />} />

            {/* Quản lý đào tạo trực tuyến */}
            <Route path="/moodle/users" element={
              <PermissionRoute requiredModule="MOODLE_USERS_MODULE">
                <MoodleUserManagementPage />
              </PermissionRoute>
            } />
            <Route path="/moodle/categories" element={
              <PermissionRoute requiredModule="CATEGORY_MODULE">
                <MoodleCategoryManagementPage />
              </PermissionRoute>
            } />
            <Route path="/moodle/courses" element={
              <PermissionRoute requiredModule="MOODLE_COURSES_MODULE">
                <MoodleCourseManagementPage />
              </PermissionRoute>
            } />

            {/* Cố vấn học tập ảo */}
            <Route path="/admin/virtual-advisor" element={
              <PermissionRoute requiredModule="VIRTUAL_ADVISOR_MODULE">
                <VirtualAdvisorManagementPage />
              </PermissionRoute>
            } />

            {/* Q&A Pairs Management */}
            <Route path="/admin/virtual-advisor/qa-pairs" element={
              <PermissionRoute requiredModule="VIRTUAL_ADVISOR_MODULE">
                <QAPairManagement />
              </PermissionRoute>
            } />
            <Route path="/admin/virtual-advisor/qa-pairs/new" element={
              <PermissionRoute requiredModule="VIRTUAL_ADVISOR_MODULE">
                <QAPairForm />
              </PermissionRoute>
            } />
            <Route path="/admin/virtual-advisor/qa-pairs/:id/edit" element={
              <PermissionRoute requiredModule="VIRTUAL_ADVISOR_MODULE">
                <QAPairForm />
              </PermissionRoute>
            } />

            {/* AI Chat Statistics */}
            <Route path="/admin/ai-chat-statistics" element={
              <PermissionRoute requiredModule="AI_CHAT_STATISTICS_MODULE">
                <AIChatStatisticsPage />
              </PermissionRoute>
            } />

            {/* AI Document Assistant (File Search) */}
            <Route path="/admin/ai-assistant" element={
              <PermissionRoute requiredModule="AI_ASSISTANT_MODULE">
                <AIAssistantAdmin />
              </PermissionRoute>
            } />

            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

function App() {
  return (
    <ConfigProvider locale={viVN} theme={{ token: { colorPrimary: '#ff7f50' } }}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/debug/login" element={<LoginTest />} />
            <Route path="/debug/auth" element={<AuthTest />} />
            <Route path="/debug/token" element={<TokenDebugPage />} />
            <Route path="/*" element={<ProtectedRoute><SidebarLayout /></ProtectedRoute>} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;